import { useState } from 'react';
import { ChevronDown, Package, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Detect & parse wizard-generated product prompts
// ---------------------------------------------------------------------------

const WIZARD_PREFIX = 'Crée un produit ';

interface WizardPromptInfo {
  type: string;
  name: string;
  sellPrice: string;
  buyPrice: string;
  category: string;
  brand: string;
  variantCount: number;
}

function parseWizardPrompt(content: string): WizardPromptInfo | null {
  if (!content.startsWith(WIZARD_PREFIX)) return null;

  const firstLine = content.split('\n')[0] ?? '';
  let type = 'standard';
  if (firstLine.includes('variable')) type = 'variable';
  else if (firstLine.includes('pack')) type = 'pack';

  const extract = (label: string): string => {
    const regex = new RegExp(`^- ${label} : (.+)$`, 'm');
    const match = content.match(regex);
    if (!match?.[1]) return '';
    // Strip IDs like (category_id: ...) or (NOUVELLE — à créer)
    return match[1]
      .replace(/\s*\((?:category_id|brand_id|supplier_id|NOUVELLE?|NOUVEAU?).*\)/, '')
      .trim();
  };

  const name = extract('Nom');
  const sellPrice = extract('Prix de vente').replace(' FCFA', '');
  const buyPrice = extract("Prix d'achat").replace(' FCFA', '');
  const category = extract('Catégorie');
  const brand = extract('Marque');

  // Count variants: lines like "  1. " or "  2. "
  const variantCount = (content.match(/^\s+\d+\.\s/gm) ?? []).length;

  return { type, name, sellPrice, buyPrice, category, brand, variantCount };
}

export function isWizardPrompt(content: string): boolean {
  return content.startsWith(WIZARD_PREFIX);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface WizardPromptCardProps {
  content: string;
}

const TYPE_LABELS: Record<string, string> = {
  standard: 'Standard',
  variable: 'Variable',
  pack: 'Pack',
};

export function WizardPromptCard({ content }: WizardPromptCardProps) {
  const { t } = useTranslation('products');
  const [showRaw, setShowRaw] = useState(false);
  const info = parseWizardPrompt(content);

  if (!info) return <p className="whitespace-pre-wrap break-words">{content}</p>;

  return (
    <div className="space-y-2">
      {/* Summary card */}
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-lime-500/20">
          <Package className="size-4 text-lime-300" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{info.name || t('create')}</span>
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium">
              {TYPE_LABELS[info.type] ?? info.type}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs opacity-80">
            {info.sellPrice && <span>{Number(info.sellPrice).toLocaleString('fr-FR')} FCFA</span>}
            {info.category && <span>{info.category}</span>}
            {info.brand && <span>{info.brand}</span>}
          </div>

          {info.type === 'variable' && info.variantCount > 0 && (
            <div className="flex items-center gap-1 text-[11px] opacity-70">
              <Sparkles className="size-3" />
              <span>
                {info.variantCount} {info.variantCount > 1 ? 'variantes' : 'variante'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Toggle raw prompt */}
      <button
        type="button"
        onClick={() => setShowRaw(!showRaw)}
        className="flex items-center gap-1 text-[11px] opacity-60 hover:opacity-90 transition-opacity"
      >
        <ChevronDown className={cn('size-3 transition-transform', showRaw && 'rotate-180')} />
        {showRaw ? 'Masquer le prompt' : 'Voir le prompt complet'}
      </button>

      {showRaw && (
        <pre className="whitespace-pre-wrap break-words text-[11px] opacity-70 leading-relaxed max-h-48 overflow-y-auto">
          {content}
        </pre>
      )}
    </div>
  );
}
